import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js";

interface SigningStatusProps {
  documentId: string;
}

interface SignaturePayload {
  status: string;
}

export function SigningStatus({ documentId }: SigningStatusProps) {
  const [status, setStatus] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      const { data, error } = await supabase
        .from('document_signatures')
        .select('status')
        .eq('document_id', documentId)
        .maybeSingle();

      if (!error && data) {
        setStatus(data.status);
      }
      setIsLoading(false);
    };

    fetchStatus();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('document-signature-status')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'document_signatures',
          filter: `document_id=eq.${documentId}`
        },
        (payload: RealtimePostgresChangesPayload<SignaturePayload>) => {
          console.log("Signature status updated:", payload);
          const newStatus = (payload.new as SignaturePayload)?.status;
          if (newStatus) {
            setStatus(newStatus);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [documentId]);

  if (isLoading) {
    return <Loader2 className="h-4 w-4 animate-spin" />;
  }

  if (!status) {
    return null;
  }

  const getStatusColor = () => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'pending':
        return 'Venter på signering';
      case 'completed':
        return 'Signert';
      case 'failed':
        return 'Signering feilet';
      default:
        return 'Ukjent status';
    }
  };

  return (
    <Badge variant="secondary" className={`${getStatusColor()} text-white`}>
      {getStatusText()}
    </Badge>
  );
}